import { forwardRef, useEffect } from "react"

import { SelectX } from "web/components"
import { isObjectWithProperty, isNonEmptyArray } from "web/services/helpers"

import ControlContainer from "./ControlContainer"

const Select = forwardRef((props, ref) => {
  const {
    id,
    kind,
    name,
    value,
    onChange,
    options,
    multiple_values,
    error,
    touched,
    onFocus,
    autoFocus,
    placeholder,
    disabled = false,
    sortSuggestions,
    onAdd,
    includeNullSuggestion = true,
    optionsLoading,
  } = props

  const testId = props.testId || `form-input-${name}`

  /**
   * Detect and correct instances where the current value
   * is no longer in the list of valid options.
   */
  useEffect(() => {
    if (multiple_values) return

    const fieldHasAValue = isObjectWithProperty(value, "value")
    const fieldHasOptions = isNonEmptyArray(options)

    const fieldValueIsNotInList =
      fieldHasAValue &&
      fieldHasOptions &&
      !options.find((opt) => opt.value === value.value)

    if (fieldValueIsNotInList) {
      onChange(null)
      return
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, value])

  const hasValue = isObjectWithProperty(value, "value")
  const displayNoValidOptions =
    !optionsLoading && !onAdd && !isNonEmptyArray(options) && !hasValue

  if (displayNoValidOptions) {
    return (
      <ControlContainer {...props}>
        <input
          className="form-control text-neutral-7"
          disabled={true}
          value="No Valid Options"
        />
      </ControlContainer>
    )
  }

  const toThickValue = (x) => ({ ...x, kind })

  function handleSelectXChange(value) {
    if (value === null) {
      onChange(null)
    } else if (multiple_values) {
      onChange(value.map(toThickValue))
    } else {
      onChange(toThickValue(value))
    }
  }

  function valueFilteredByOptions() {
    const blankValue = multiple_values ? [] : null

    const matchesOption = (selected) =>
      options.some((option) => option.value === selected.value)

    if (value && multiple_values) {
      return value.filter(matchesOption)
    } else if (value && matchesOption(value)) {
      return value
    } else {
      return blankValue
    }
  }

  return (
    <ControlContainer {...props} errorCondition={error && touched}>
      <SelectX
        id={id}
        ref={ref}
        name={name}
        validValues={options}
        value={valueFilteredByOptions()}
        onChange={handleSelectXChange}
        onFocus={onFocus}
        onAdd={onAdd}
        options={{
          multipleValues: multiple_values || false,
          inputClassName: "form-control select",
          inputTestId: `${testId}`,
          autoFocus,
          placeholder:
            placeholder ||
            (multiple_values ? "Select values..." : "Select a value..."),
          sortSuggestions,
          suggestionDelay: 50,
          includeNullSuggestion: includeNullSuggestion,
          optionsLoading,
        }}
        disabled={disabled}
      />
    </ControlContainer>
  )
})

export default Select
